import { styled } from '@linaria/react';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import MapboxGlMap, { MapRef, TransformRequestFunction } from 'react-map-gl';
import { InitReferenceCommand } from '../../../core/reference/commands/InitReferenceCommand';
import { loadingRoadSectionsQuery, roadSectionsQuery } from '../../../core/reference/queries';
import { basemapQuery, organizationBoundsQuery } from '../../../core/mapV2/queries';
import { loadingSessionsQuery, sessionsQuery } from '../../../core/sessions/queries';
import { InitSessionsCommand } from '../../../core/sessions/commands/InitSessionsCommand';
import { useAppDispatch, useAppSelector } from '../../../types';
import { useConfig } from '../../config';
import { Legend } from './Legend';
import { MapControls } from './MapControls';
import { ReferenceLayers } from './ReferenceLayers';
import { SectionContextMenu } from './SectionContextMenu';
import { useReferenceColoring } from './useReferenceColoring';
import { useReferenceInteractions } from './useReferenceInteractions';
import { BASEMAP_STYLES, DEFAULT_VIEW, FIT_BOUNDS_OPTIONS, MAX_ZOOM, MIN_ZOOM } from './map-styles';

const Container = styled.div`
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
`;

// Mapbox canvas of the map v2 page: the base map, its controls and the layers (référentiel for now,
// sessions and the others in later phases).
// It deliberately does not use `reuseMaps`, so it never shares a Mapbox instance with the current /map.
export const MapCanvas: FC = () => {
    const { mapboxToken, backendUrl } = useConfig();
    const dispatch = useAppDispatch();
    const mapRef = useRef<MapRef | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [loaded, setLoaded] = useState(false);
    const fitted = useRef(false);
    const basemap = useAppSelector(basemapQuery);
    const bounds = useAppSelector(organizationBoundsQuery);
    const sessions = useAppSelector(sessionsQuery);
    const loadingSessions = useAppSelector(loadingSessionsQuery);
    const roadSections = useAppSelector(roadSectionsQuery);
    const loadingRoadSections = useAppSelector(loadingRoadSectionsQuery);

    const { contextMenu, closeContextMenu, ...interactions } = useReferenceInteractions(mapRef, loaded);
    useReferenceColoring(mapRef, loaded);

    // Tiles come from the API and need the session cookie.
    const transformRequest: TransformRequestFunction = useCallback(
        (url) => (url.startsWith(backendUrl!) ? { url, credentials: 'include' } : { url }),
        [backendUrl],
    );

    // The sessions list gives the territory's extent (and is needed by the sessions layer later).
    useEffect(() => {
        if (sessions.length === 0 && !loadingSessions) {
            dispatch(InitSessionsCommand());
        }
        // Only on mount: an organization without any session must not be re-fetched in a loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The référentiel's sections (attributes and selection), joined to the map tiles by id.
    useEffect(() => {
        if (roadSections.length === 0 && !loadingRoadSections) {
            dispatch(InitReferenceCommand());
        }
        // Only on mount: an organization without any referential must not be re-fetched in a loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Frame the territory once, as soon as both the map and its extent are available.
    useEffect(() => {
        if (loaded && bounds && !fitted.current) {
            fitted.current = true;
            mapRef.current?.fitBounds(bounds, FIT_BOUNDS_OPTIONS);
        }
    }, [loaded, bounds]);

    // Mapbox only listens to window resizes: tell it when the panels slide or collapse.
    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(() => mapRef.current?.resize());
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    return (
        <Container ref={containerRef} data-testid="map-v2-map-canvas">
            <MapboxGlMap
                ref={mapRef}
                mapboxAccessToken={mapboxToken}
                mapStyle={BASEMAP_STYLES[basemap]}
                initialViewState={DEFAULT_VIEW}
                minZoom={MIN_ZOOM}
                maxZoom={MAX_ZOOM}
                attributionControl={false}
                onLoad={() => setLoaded(true)}
                transformRequest={transformRequest}
                {...interactions}
                style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}
            >
                <ReferenceLayers />
                <MapControls />
            </MapboxGlMap>
            <Legend />
            <SectionContextMenu target={contextMenu} onClose={closeContextMenu} />
        </Container>
    );
};
